import {
  decodeGeometry,
  readVectorTile,
  VectorTileFeature,
  VectorTileGeometryType,
  VectorTileLayer,
} from "./world-map-vector-tile";

/** The tiles are drawn at 256 px, half of the 512 px the style zoom levels refer to */
const STYLE_ZOOM_OFFSET = -1;
const MAX_CACHED_SOURCE_TILES = 48;
const HEX_SHORT_LENGTHS = new Set([3, 4]);
const MAX_CHANNEL = 255;
const HTTP_NOT_FOUND = 404;
const LINE_CAPS: CanvasLineCap[] = ["butt", "round", "square"];
const LINE_JOINS: CanvasLineJoin[] = ["miter", "round", "bevel"];

const GEOMETRY_TYPE_NAMES: Record<number, string> = {
  [VectorTileGeometryType.point]: "Point",
  [VectorTileGeometryType.line]: "LineString",
  [VectorTileGeometryType.polygon]: "Polygon",
};

type StyleLayer = {
  id: string;
  type: string;
  source?: string;
  sourceLayer?: string;
  filter?: unknown;
  minzoom: number;
  maxzoom: number;
  paint: Record<string, unknown>;
  layout: Record<string, unknown>;
};

type VectorSource = { url: string; tileTemplate: string; maxZoom: number };

/** A Mapbox GL style, reduced to what is needed to draw its fills and lines */
export type VectorStyle = {
  layers: StyleLayer[];
  sources: Map<string, VectorSource>;
};

type Rgba = [number, number, number, number];

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isStop(value: unknown): value is [number, unknown] {
  return (
    Array.isArray(value) && value.length === 2 && typeof value[0] === "number"
  );
}

/**
 * @param color A CSS color in hex, rgb() or rgba() notation
 * @returns The color channels, alpha from 0 to 1; undefined for other notations
 */
export function parseColor(color: string): Rgba | undefined {
  const hex = /^#([\da-f]{3,4}|[\da-f]{6}|[\da-f]{8})$/i.exec(color)?.[1];
  if (hex) {
    const [r, g, b, a = "ff"] = HEX_SHORT_LENGTHS.has(hex.length)
      ? [...hex].map((digit) => digit + digit)
      : (hex.match(/../g) ?? []);
    return [
      parseInt(r, 16),
      parseInt(g, 16),
      parseInt(b, 16),
      parseInt(a, 16) / MAX_CHANNEL,
    ];
  }
  const channels = /^rgba?\(([^)]+)\)$/i
    .exec(color)?.[1]
    .split(",")
    .map(Number);
  if (!channels || channels.length < 3 || channels.some(Number.isNaN)) {
    return undefined;
  }
  const [r, g, b, a = 1] = channels;
  return [r, g, b, a];
}

function interpolate(from: unknown, to: unknown, t: number): unknown {
  if (typeof from === "number" && typeof to === "number") {
    return from + (to - from) * t;
  }
  const fromColor = typeof from === "string" ? parseColor(from) : undefined;
  const toColor = typeof to === "string" ? parseColor(to) : undefined;
  if (fromColor && toColor) {
    const [r, g, b, a] = fromColor.map(
      (channel, i) => channel + (toColor[i] - channel) * t,
    );
    return `rgba(${Math.round(r)},${Math.round(g)},${Math.round(b)},${a})`;
  }
  return from;
}

/**
 * @param value A style property: a constant, or a zoom function with stops and an optional base
 * @param zoom The style zoom level
 * @returns The value at the zoom level, interpolated between the stops where possible
 */
export function evaluateStyleValue(value: unknown, zoom: number): unknown {
  if (!isRecord(value) || !Array.isArray(value.stops)) return value;
  const stops = value.stops.filter(isStop);
  const base = typeof value.base === "number" ? value.base : 1;
  const last = stops.at(-1);
  if (!last) return undefined;
  const next = stops.findIndex(([stopZoom]) => zoom < stopZoom);
  if (next === -1) return last[1];
  if (next === 0) return stops[0][1];
  const [z0, v0] = stops[next - 1];
  const [z1, v1] = stops[next];
  const t =
    base === 1
      ? (zoom - z0) / (z1 - z0)
      : (base ** (zoom - z0) - 1) / (base ** (z1 - z0) - 1);
  return interpolate(v0, v1, t);
}

function featureValue(
  feature: Pick<VectorTileFeature, "type" | "properties">,
  key: unknown,
): unknown {
  if (key === "$type") return GEOMETRY_TYPE_NAMES[feature.type];
  return typeof key === "string" ? feature.properties[key] : undefined;
}

/**
 * @param filter A legacy Mapbox GL filter
 * @param feature The feature to test
 * @returns True if the feature passes the filter; unsupported filters pass everything
 */
export function matchesFilter(
  filter: unknown,
  feature: Pick<VectorTileFeature, "type" | "properties">,
): boolean {
  if (!Array.isArray(filter) || filter.length === 0) return true;
  const [operator, key, ...values] = filter;
  const value = featureValue(feature, key);
  const [expected] = values;
  switch (operator) {
    case "all":
      return filter.slice(1).every((part) => matchesFilter(part, feature));
    case "any":
      return filter.slice(1).some((part) => matchesFilter(part, feature));
    case "none":
      return !filter.slice(1).some((part) => matchesFilter(part, feature));
    case "==":
      return value === expected;
    case "!=":
      return value !== expected;
    case "in":
      return values.includes(value);
    case "!in":
      return !values.includes(value);
    case "has":
      return value !== undefined;
    case "!has":
      return value === undefined;
    case "<":
    case "<=":
    case ">":
    case ">=": {
      if (typeof value !== "number" || typeof expected !== "number") {
        return false;
      }
      if (operator === "<") return value < expected;
      if (operator === "<=") return value <= expected;
      return operator === ">" ? value > expected : value >= expected;
    }
    default:
      return true;
  }
}

function parseLayer(layer: unknown): StyleLayer | undefined {
  if (
    !isRecord(layer) ||
    typeof layer.id !== "string" ||
    typeof layer.type !== "string"
  ) {
    return undefined;
  }
  return {
    id: layer.id,
    type: layer.type,
    source: typeof layer.source === "string" ? layer.source : undefined,
    sourceLayer:
      typeof layer["source-layer"] === "string"
        ? layer["source-layer"]
        : undefined,
    filter: layer.filter,
    minzoom: typeof layer.minzoom === "number" ? layer.minzoom : 0,
    maxzoom: typeof layer.maxzoom === "number" ? layer.maxzoom : Infinity,
    paint: isRecord(layer.paint) ? layer.paint : {},
    layout: isRecord(layer.layout) ? layer.layout : {},
  };
}

async function fetchJson(url: string): Promise<unknown> {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`${url} returned ${response.status}`);
  return response.json();
}

async function loadSource(url: string): Promise<VectorSource> {
  const info = await fetchJson(`${url}?f=json`);
  const template =
    isRecord(info) && Array.isArray(info.tiles) ? info.tiles[0] : undefined;
  const maxZoom = isRecord(info) ? (info.maxLOD ?? info.maxzoom) : undefined;
  if (typeof template !== "string" || typeof maxZoom !== "number") {
    throw new Error(`Invalid vector tile service ${url}`);
  }
  return { url, tileTemplate: template, maxZoom };
}

const styles = new Map<string, Promise<VectorStyle>>();

/**
 * @param url The URL of an Esri vector basemap style (root.json)
 * @returns The style with its vector tile sources, loaded once
 */
export function loadVectorStyle(url: string): Promise<VectorStyle> {
  const cached = styles.get(url);
  if (cached) return cached;
  const promise = fetchJson(url).then(async (style) => {
    if (!isRecord(style) || !isRecord(style.sources)) {
      throw new Error(`Invalid style ${url}`);
    }
    const sources = new Map<string, VectorSource>();
    for (const [name, source] of Object.entries(style.sources)) {
      if (isRecord(source) && typeof source.url === "string") {
        sources.set(name, await loadSource(source.url));
      }
    }
    const layers = Array.isArray(style.layers)
      ? style.layers.map(parseLayer).filter((layer) => !!layer)
      : [];
    return { layers, sources };
  });
  promise.catch(() => styles.delete(url));
  styles.set(url, promise);
  return promise;
}

type SourceTile = {
  layers: Map<string, VectorTileLayer>;
  z: number;
  x: number;
  y: number;
};

const sourceTiles = new Map<string, Promise<SourceTile>>();

/**
 * @param source The vector tile service
 * @param z Zoom level
 * @param x Tile column
 * @param y Tile row
 * @returns The vector tile, or its closest available ancestor where the service has no data
 */
function loadSourceTile(
  source: VectorSource,
  z: number,
  x: number,
  y: number,
): Promise<SourceTile> {
  const url = `${source.url}/${source.tileTemplate}`
    .replace("{z}", String(z))
    .replace("{x}", String(x))
    .replace("{y}", String(y));
  const cached = sourceTiles.get(url);
  if (cached) return cached;
  const promise = fetch(url).then(async (response) => {
    if (response.status === HTTP_NOT_FOUND && z > 0) {
      return loadSourceTile(source, z - 1, x >> 1, y >> 1);
    }
    if (!response.ok) throw new Error(`${url} returned ${response.status}`);
    const layers = readVectorTile(new Uint8Array(await response.arrayBuffer()));
    return { layers, z, x, y };
  });
  promise.catch(() => sourceTiles.delete(url));
  sourceTiles.set(url, promise);
  if (sourceTiles.size > MAX_CACHED_SOURCE_TILES) {
    sourceTiles.delete(sourceTiles.keys().next().value ?? url);
  }
  return promise;
}

function stringValue(value: unknown, fallback: string): string {
  return typeof value === "string" ? value : fallback;
}

function numberValue(value: unknown, fallback: number): number {
  return typeof value === "number" ? value : fallback;
}

/**
 * Draws the fills and lines of a style into a tile canvas, on top of its current content.
 * Labels, icons and fill patterns are not drawn.
 *
 * @param style The style to draw
 * @param tile The Web Mercator tile the canvas shows
 * @param tile.z Zoom level
 * @param tile.x Tile column
 * @param tile.y Tile row
 * @param canvas A square canvas showing the tile
 */
export async function drawVectorStyle(
  style: VectorStyle,
  { z, x, y }: { z: number; x: number; y: number },
  canvas: HTMLCanvasElement,
): Promise<void> {
  const context = canvas.getContext("2d");
  if (!context) return;
  const size = canvas.width;
  const zoom = z + STYLE_ZOOM_OFFSET;
  const layers = style.layers.filter(
    (layer) =>
      layer.layout.visibility !== "none" &&
      zoom >= layer.minzoom &&
      zoom < layer.maxzoom &&
      (layer.type === "background" ||
        layer.type === "line" ||
        (layer.type === "fill" && !layer.paint["fill-pattern"])),
  );

  const tiles = new Map<string, SourceTile>();
  await Promise.all(
    [...new Set(layers.map((layer) => layer.source))].map(async (name) => {
      const source = name ? style.sources.get(name) : undefined;
      if (!name || !source) return;
      const sourceZoom = Math.max(0, Math.min(zoom, source.maxZoom));
      const shift = z - sourceZoom;
      tiles.set(
        name,
        await loadSourceTile(source, sourceZoom, x >> shift, y >> shift),
      );
    }),
  );

  for (const layer of layers) {
    const color = evaluateStyleValue(layer.paint[`${layer.type}-color`], zoom);
    context.globalAlpha = numberValue(
      evaluateStyleValue(layer.paint[`${layer.type}-opacity`], zoom),
      1,
    );
    context.fillStyle = stringValue(color, "#000");
    context.strokeStyle = stringValue(color, "#000");
    if (layer.type === "background") {
      context.fillRect(0, 0, size, size);
      continue;
    }

    const tile = layer.source ? tiles.get(layer.source) : undefined;
    const data = layer.sourceLayer && tile?.layers.get(layer.sourceLayer);
    if (!tile || !data) continue;
    const factor = 2 ** (z - tile.z);
    const scale = (size * factor) / data.extent;
    const offsetX = (tile.x * factor - x) * size;
    const offsetY = (tile.y * factor - y) * size;

    const path = new Path2D();
    for (const feature of data.features) {
      if (
        feature.type === VectorTileGeometryType.point ||
        !matchesFilter(layer.filter, feature)
      ) {
        continue;
      }
      for (const part of decodeGeometry(feature.geometry)) {
        part.forEach((point, index) => {
          const px = point.x * scale + offsetX;
          const py = point.y * scale + offsetY;
          if (index === 0) path.moveTo(px, py);
          else path.lineTo(px, py);
        });
        if (feature.type === VectorTileGeometryType.polygon) path.closePath();
      }
    }

    if (layer.type === "fill") {
      context.fill(path);
      continue;
    }
    const width = numberValue(
      evaluateStyleValue(layer.paint["line-width"], zoom),
      1,
    );
    const dashes = evaluateStyleValue(layer.paint["line-dasharray"], zoom);
    const cap = evaluateStyleValue(layer.layout["line-cap"], zoom);
    const join = evaluateStyleValue(layer.layout["line-join"], zoom);
    context.lineWidth = width;
    context.lineCap = LINE_CAPS.find((value) => value === cap) ?? "butt";
    context.lineJoin = LINE_JOINS.find((value) => value === join) ?? "miter";
    context.setLineDash(
      Array.isArray(dashes)
        ? dashes.map((dash) => numberValue(dash, 0) * width)
        : [],
    );
    context.stroke(path);
  }
  context.globalAlpha = 1;
  context.setLineDash([]);
}
